import { makeObservable, observable } from 'mobx'

import DateInterval from '~/client/src/shared/types/DateInterval'
import Guard from '~/client/src/shared/utils/Guard'

import CalendarEventLabelType from '../enums/CalendarEventLabelType'
import ProjectDateStore from '../stores/ui/ProjectDate.store'
import { SharedInfo } from './SharedInfo'

import Colors from '~/client/src/shared/theme.module.scss'

export interface ICalendarDates {
  startDate: Date
  endDate: Date
}

export enum CalendarEventEntityType {
  Delivery = 'Delivery',
  Form = 'Form',
}

export const NEW_EVENT_DATA_ID = 'new-event'
const NEW_EVENT = {
  labelType: CalendarEventLabelType.New,
  dataId: NEW_EVENT_DATA_ID,
  className: 'selected-event',
}

export default class CalendarEvent {
  public sharedInfo: SharedInfo
  public isShared: boolean

  public static createNewEvent(
    startDate: Date,
    endDate: Date,
    projectDateStore: ProjectDateStore,
    entityType: CalendarEventEntityType,
    isShared?: boolean,
    sharedInfo?: SharedInfo,
  ) {
    return CalendarEvent.createEvent({
      startDate,
      endDate,
      entityType,
      ...NEW_EVENT,
      projectDateStore,
      styles: { backgroundColor: Colors.neutral0, color: Colors.neutral100 },
      isShared,
      sharedInfo,
    })
  }

  public static createEvent({
    startDate,
    endDate,
    entityType,
    labelType,
    dataId,
    projectDateStore,
    className,
    styles,
    data,
    label,
    isActive,
    isAllDay,
    isShared = false,
    sharedInfo,
  }: {
    startDate: Date
    endDate: Date
    entityType: CalendarEventEntityType
    labelType: CalendarEventLabelType
    dataId: string
    projectDateStore: ProjectDateStore
    className?: string
    styles?: any
    data?: any
    label?: string
    isActive?: boolean
    isAllDay?: boolean
    isShared?: boolean
    sharedInfo?: SharedInfo
  }): CalendarEvent {
    return new CalendarEvent(
      startDate,
      endDate,
      entityType,
      labelType,
      dataId,
      projectDateStore,
      className,
      styles,
      data,
      label,
      isActive,
      isAllDay,
      isShared,
      sharedInfo,
    )
  }

  @observable public zoneId: string = null
  @observable public areaId: string = null
  @observable public buildingId: string = null
  @observable public gateId: string = null
  @observable public levelId: string = null
  @observable public routeId: string = null
  @observable public equipmentIds: string[] = []
  @observable public isAllDay: boolean = null
  public dateInterval: DateInterval

  public constructor(
    startDate: Date,
    endDate: Date,
    public entityType: CalendarEventEntityType,
    public labelType: CalendarEventLabelType,
    public dataId: string,
    projectDateStore: ProjectDateStore,
    public className?: string,
    public styles?: any,
    public data?: any,
    public label?: string,
    public isActive?: boolean,
    isAllDay?: boolean,
    isShared?: boolean,
    sharedInfo?: SharedInfo,
  ) {
    Guard.requireAll({ startDate, endDate, labelType, dataId })
    this.dateInterval = new DateInterval(startDate, endDate, projectDateStore)
    this.isAllDay = !!isAllDay
    this.isShared = isShared
    this.sharedInfo = sharedInfo
    makeObservable(this)
  }

  public get startDate(): Date {
    return this.dateInterval.startDate
  }

  public set startDate(newDate: Date) {
    this.dateInterval.setStartDate(newDate)
  }

  public get endDate(): Date {
    return this.dateInterval.endDate
  }

  public set endDate(newDate: Date) {
    this.dateInterval.setEndDate(newDate)
  }

  public isEqual(other: CalendarEvent) {
    return this === other
  }

  public isIntersect(other: CalendarEvent) {
    return this.dateInterval.isIntersect(other.dateInterval)
  }

  public isInHour(date: Date, hour: number) {
    return this.dateInterval.isInHour(date, hour)
  }

  public get isNew(): boolean {
    return this.dataId === NEW_EVENT_DATA_ID
  }
}
